/**
 * Firebase Auth providers: Google/GitHub provider creation, signInWithPopup with
 * account-exists-with-different-credential linking, reauth provider selection.
 */

import type { FirebaseAuthInstance, FirebaseUser, FirebaseAuthProvider } from './auth-firebase-load';

export function getAlternateProvider(
  getAuth: () => FirebaseAuthInstance,
  failedProviderId: string
): unknown {
  const auth = getAuth();
  if (failedProviderId === 'github.com' && auth.GoogleAuthProvider) {
    return new (auth as { GoogleAuthProvider: new () => unknown }).GoogleAuthProvider();
  }
  if (failedProviderId === 'google.com' && auth.GithubAuthProvider) {
    return new (auth as { GithubAuthProvider: new () => unknown }).GithubAuthProvider();
  }
  return null;
}

export function signInWithPopupAndLink(
  getAuth: () => FirebaseAuthInstance,
  provider: unknown,
  pendingCred: { get: () => unknown; set: (c: unknown) => void; clear: () => void },
  onSuccess: () => void,
  onError: (err: Error) => void
): void {
  const auth = getAuth();
  auth.signInWithPopup(provider)
    .then((result: { user: FirebaseUser }) => {
      const user = result?.user;
      const cred = pendingCred.get();
      if (cred && user?.linkWithCredential) {
        pendingCred.clear();
        user.linkWithCredential(cred).then(onSuccess).catch(() => onSuccess());
      } else {
        onSuccess();
      }
    })
    .catch((err: { code?: string; credential?: unknown; message?: string; providerId?: string }) => {
      if (err?.code === 'auth/account-exists-with-different-credential' && err.credential) {
        pendingCred.set(err.credential);
        const alt = getAlternateProvider(getAuth, err.providerId || '');
        if (!alt) {
          onError(new Error('Please sign in with your other account first to link providers.'));
          return;
        }
        auth.signInWithPopup(alt)
          .then((result: { user: FirebaseUser }) => result?.user?.linkWithCredential?.(err.credential as object))
          .then(() => {
            pendingCred.clear();
            onSuccess();
          })
          .catch((linkErr: { code?: string }) => {
            if (linkErr?.code === 'auth/popup-blocked') {
              onError(new Error('Pop-up blocked. Allow pop-ups and try again to link your accounts.'));
            } else {
              pendingCred.clear();
              onError(linkErr instanceof Error ? linkErr : new Error(String(linkErr)));
            }
          });
      } else {
        onError(err instanceof Error ? err : new Error(err?.message || 'Sign-in failed'));
      }
    });
}

export interface AuthNamespace {
  GoogleAuthProvider?: new () => FirebaseAuthProvider;
  GithubAuthProvider?: new () => unknown;
}

export function getReauthProvider(authNamespace: AuthNamespace, user: FirebaseUser): unknown {
  const providers = user?.providerData || [];
  for (let i = 0; i < providers.length; i++) {
    const pid = providers[i]?.providerId;
    if (pid === 'google.com' && authNamespace.GoogleAuthProvider) {
      const p = new authNamespace.GoogleAuthProvider();
      if (user?.email && p.setCustomParameters) {
        p.setCustomParameters({ login_hint: user.email });
      }
      return p;
    }
    if (pid === 'github.com' && authNamespace.GithubAuthProvider) {
      return new authNamespace.GithubAuthProvider();
    }
  }
  return null;
}
